import React, { ReactNode } from 'react'
import { GlobalCard, GlobalCardContent } from '../common/global-card'
import {
    DocumentIcon,
    NewsCardIcon,
    CalendarOutlineIcon,
    BriefcaseIcon,
} from '@/components/icons/react'
import { TrendingUp, ChevronRight } from 'lucide-react'
import { FilingCard } from '../filings/filing-card'
import type { CompanyFilingView } from '@/services/filings/types'
import { EarningTranscriptCard } from '../earning-transcripts/earning-transcript-card'
import type { LatestEarningTranscript } from '@/services/earning-transcript'
import { AnalysisCacheProvider } from '@/contexts/analysis-cache-context'
import StockCard from '../ai/stock-card'
import type { KeyMetrics, StockPriceData } from '@/types/stock-card'

type PreviewCardProps = {
    badgeText?: string
    title: string
    body: ReactNode
    footerText?: string
}

const OthersList = () => (
    <div className="w-[420px] h-[530px] flex flex-col">
        <div className="flex flex-1 flex-col gap-5">
            {others.map(({ id, label, icon: Icon, ai }) => (
                <GlobalCard
                    key={id}
                    className="rounded-[32px]! p-5 shadow-none! border border-black/2 flex-1"
                >
                    <GlobalCardContent className="flex h-full items-center gap-2.5">
                        <div className="flex items-center gap-5">
                            <div className="h-5 w-5 rounded-2xl bg-sidebar flex items-center justify-center text-black/40">
                                <Icon className="size-4" />
                            </div>
                            <span className="font-['Inter'] font-medium text-[14px] leading-[20px] text-black/80 align-middle">
                                {label}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            {ai && (
                                <div className="rounded-full h-[19px] w-[19px] flex items-center justify-center border border-[#0a84ff60] bg-[#0A84FF]/10">
                                    <span className="text-[9px] leading-[11px] text-[#0A84FF]">
                                        AI
                                    </span>
                                </div>
                            )}
                            <span className="text-[10px] leading-[14px] text-[#0A84FF]">
                                Next Update
                            </span>
                        </div>
                    </GlobalCardContent>
                </GlobalCard>
            ))}
        </div>
    </div>
)

const PreviewCard = ({ badgeText, title, body, footerText }: PreviewCardProps) => (
    <GlobalCard className="max-w-[414px] flex flex-col justify-between h-[530px] p-5! rounded-[32px]!">
        <GlobalCardContent className="flex flex-col gap-2.5 p-2.5">
            {badgeText && (
                <span className="bg-white border-[0.5px] shadow-xs border-black/6 text-black/80 rounded-[23px] inline-flex items-center gap-[3px] pl-1.5 pr-3 py-1.5 font-medium text-[12px] leading-[100%] font-['Inter'] w-fit">
                    <DocumentIcon aria-hidden="true" />
                    {badgeText}
                </span>
            )}
            <h2 className="text-[17px] leading-[27px] font-semibold align-middle text-black/80">
                {title}
            </h2>
        </GlobalCardContent>
        <GlobalCardContent>{body}</GlobalCardContent>
        {footerText && (
            <button className="flex cursor-pointer border-[0.5px]! shadow-xs border-black/6 hover:bg-black/2 py-3 pl-5 pr-3.5 rounded-[23px] w-fit items-center gap-1 bg-white font-['Inter'] font-medium text-[12px] leading-[100%] tracking-[0] not-italic">
                <h2 className="text-black/80">{footerText}</h2>
                <ChevronRight className="text-black/40 size-2.5" />
            </button>
        )}
    </GlobalCard>
)

const filingPreviewCards: Array<{ filing: CompanyFilingView; label: string }> = [
    {
        label: 'Q1',
        filing: {
            company_id: 1,
            symbol: 'AAPL',
            company_name: 'Apple Inc.',
            logo: null,
            filing_id: 1,
            accession_number: 'preview-filing-q1',
            form_type: '10-K',
            description: 'This is Demo Txt that are place Holders only',
            filed_at: '2024-02-01',
        },
    },
    {
        label: 'Q2',
        filing: {
            company_id: 1,
            symbol: 'AAPL',
            company_name: 'Apple Inc.',
            logo: null,
            filing_id: 2,
            accession_number: 'preview-filing-q2',
            form_type: '10-K',
            description: 'This is Demo Txt that are place Holders only',
            filed_at: '2024-05-01',
        },
    },
]

const earningTranscriptPreviews: Array<{ transcript: LatestEarningTranscript; label: string }> = [
    {
        label: 'Q1',
        transcript: {
            id: 1,
            symbol_id: 1,
            symbol: 'AAPL',
            company_name: 'Apple Inc.',
            date: '2024-03-15',
            period: 'Q1',
            fiscal_year: 2024,
            content_preview: 'Preview transcript content placeholder',
        },
    },
    {
        label: 'Q2',
        transcript: {
            id: 2,
            symbol_id: 1,
            symbol: 'AAPL',
            company_name: 'Apple Inc.',
            date: '2024-06-15',
            period: 'Q2',
            fiscal_year: 2024,
            content_preview: 'Preview transcript content placeholder',
        },
    },
]

const watchlistPreviewMetrics: KeyMetrics = {
    marketCap: '$2.89T',
    pe: '28.4',
    roe: '32.1%',
    evSales: '7.3x',
    roa: '15.2%',
    fcfYield: '4.3%',
}

const watchlistPreviewHistory: StockPriceData[] = [
    {
        date: '2024-01-01',
        open: 180,
        high: 182,
        low: 179,
        close: 181,
        adjusted_open: 180,
        adjusted_high: 182,
        adjusted_low: 179,
        adjusted_close: 181,
        volume: 1200000,
    },
    {
        date: '2024-01-02',
        open: 181,
        high: 183,
        low: 180,
        close: 182,
        adjusted_open: 181,
        adjusted_high: 183,
        adjusted_low: 180,
        adjusted_close: 182,
        volume: 1000000,
    },
]

const watchlistPreviewCards = [
    {
        id: 'aapl',
        symbol: 'AAPL',
        companyId: 1,
        companyName: 'Apple Inc.',
        exchange: 'NASDAQ',
        history: watchlistPreviewHistory,
        metrics: watchlistPreviewMetrics,
    },
    {
        id: 'msft',
        symbol: 'MSFT',
        companyId: 2,
        companyName: 'Microsoft Corp.',
        exchange: 'NASDAQ',
        history: watchlistPreviewHistory,
        metrics: {
            ...watchlistPreviewMetrics,
            marketCap: '$3.02T',
            pe: '31.2',
        },
    },
]

const others = [
    { id: 'news', label: 'News', icon: NewsCardIcon, ai: true },
    {
        id: 'insider',
        label: 'Insider, Institutional Trades & Holdings',
        icon: TrendingUp,
    },
    {
        id: 'politicians',
        label: 'Politicians Trades & Holdings',
        icon: TrendingUp,
    },
    { id: 'calendar', label: 'Calendar', icon: CalendarOutlineIcon },
    { id: 'portfolio', label: 'Portfolio sync', icon: BriefcaseIcon },
]

const previewCards: Array<PreviewCardProps & { id: string }> = [
    {
        id: 'sec-filings',
        badgeText: 'Sec Filings',
        title: 'Analysis SEC Filings into clear insights, instantly.',
        body: (
            <div className="relative overflow-hidden">
                <div className="pointer-events-none absolute top-0 right-0 bottom-0 w-[30%] bg-gradient-to-r from-transparent via-sidebar/80 to-sidebar z-10" />
                <div className="grid grid-cols-2 gap-5 relative z-0">
                    {filingPreviewCards.map(({ filing, label }) => (
                        <div key={filing.accession_number} className="flex flex-col gap-2">
                            <FilingCard
                                filing={filing}
                                onClick={(preview) => {
                                    void preview
                                }}
                            />
                            <span className="text-xs text-black/60 text-center font-medium tracking-tight">
                                {label}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        ),
        footerText: 'Learn more',
    },
    {
        id: 'earnings-transcript',
        badgeText: 'Earnings Transcript',
        title: 'Analysis SEC Fillings into clear insights, instantly.',
        body: (
            <AnalysisCacheProvider>
                <div className="relative overflow-hidden px-6">
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[50%] bg-gradient-to-t from-sidebar via-sidebar/80 to-transparent z-10" />
                    <div className="grid grid-cols-1 gap-4 relative z-0">
                        {earningTranscriptPreviews.map(({ transcript, label }) => (
                            <div key={transcript.id} className="flex flex-col gap-2">
                                <EarningTranscriptCard
                                    transcript={transcript}
                                    symbol="AAPL"
                                    onChat={() => {}}
                                    onViewTranscript={() => {}}
                                />
                                <span className="text-xs text-black/60 text-center font-medium tracking-tight">
                                    {label}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </AnalysisCacheProvider>
        ),
        footerText: 'Learn more',
    },
    {
        id: 'watchlist',
        badgeText: 'Watchlist',
        title: 'Analysis SEC Fillings into clear insights, instantly.',
        body: (
            <div className="relative overflow-hidden">
                <div className="pointer-events-none absolute inset-y-0 right-0 w-[26%] bg-gradient-to-l from-sidebar via-sidebar/80 to-transparent z-10" />
                <div className="flex gap-4 relative z-0">
                    {watchlistPreviewCards.map(
                        ({ id, symbol, companyId, companyName, exchange, history, metrics }) => {
                            const latest = history[history.length - 1]
                            return (
                                <StockCard
                                    key={id}
                                    symbol={symbol}
                                    companyId={companyId}
                                    companyName={companyName}
                                    exchange={exchange}
                                    found={true}
                                    total_records={history.length}
                                    date_range={{
                                        latest: history[history.length - 1]?.date ?? '',
                                        earliest: history[0]?.date ?? '',
                                    }}
                                    latest_price={{
                                        date: latest?.date ?? '',
                                        price: latest?.close ?? 0,
                                        open: latest?.open ?? 0,
                                        high: latest?.high ?? 0,
                                        low: latest?.low ?? 0,
                                        close: latest?.close ?? 0,
                                        adjusted_close: latest?.adjusted_close ?? 0,
                                        volume: latest?.volume ?? 0,
                                    }}
                                    historical_data={history}
                                    message=""
                                    keyMetrics={metrics}
                                    className="shadow-none border border-black/10"
                                    chartWidth={240}
                                />
                            )
                        }
                    )}
                </div>
            </div>
        ),
        footerText: 'Try for free',
    },
]

const PreviewCardSection = () => {
    return (
        <div className="p-20 mx-5">
            <h2 className="text-[22px] leading-[26px] font-bold text-center mb-5 md:mb-16">
                Everything in one Place
            </h2>
            <div className="grid gap-7.5 justify-center max-w-[970px] grid-cols-[repeat(auto-fit,minmax(450px,max-content))] mx-auto">
                {previewCards.map(({ id, ...card }) => (
                    <PreviewCard key={id} {...card} />
                ))}
                <OthersList />
            </div>
        </div>
    )
}

export default PreviewCardSection
